import type {
  PolymarketMarketSummary,
  PolymarketMarketDetail,
  PolymarketMarketListResponse,
  PolymarketMarketDetailResponse,
  PolymarketMeSummaryResponse,
  PolymarketPositionListResponse,
  PolymarketQuoteRequest,
  PolymarketQuoteResponse,
  PolymarketParticipationRequest,
  PolymarketParticipationResponse,
  PolymarketPersonalization,
  PolymarketPositionListItem,
  PolymarketProposalRequest,
  PolymarketProposalResponse,
  PolymarketMyProposalsResponse,
} from '../model/types';

const baseWallet = {
  availableBalance: 12800,
  lockedBalance: 4200,
  pendingSettlementBalance: 1700,
} as const;

const personalizationByMarketId: Record<string, PolymarketPersonalization | null> = {
  'btc-110k-mar': {
    wallet: { ...baseWallet },
    eligibility: {
      canParticipate: true,
      reasonCode: null,
      reasonMessage: null,
      minBetAmount: 100,
      maxBetAmount: 5000,
      maxBetPerUser: 10000,
      perBetLimit: 2000,
    },
    myPosition: {
      side: 'YES',
      amount: 1200,
      estimatedPayout: 2145,
      status: 'PLACED',
      result: 'ACTIVE',
      finalPayout: null,
    },
  },
  'fed-cut-june': {
    wallet: { ...baseWallet },
    eligibility: {
      canParticipate: false,
      reasonCode: 'ACCOUNT_NOT_ELIGIBLE',
      reasonMessage: '신규 계정은 운영 검토 후 참여할 수 있습니다.',
      minBetAmount: 300,
      maxBetAmount: 3000,
      maxBetPerUser: 5000,
      perBetLimit: 1000,
    },
    myPosition: {
      side: null,
      amount: 0,
      estimatedPayout: 0,
      status: null,
      result: null,
      finalPayout: null,
    },
  },
  'eth-etf-q2': {
    wallet: { ...baseWallet },
    eligibility: {
      canParticipate: false,
      reasonCode: 'MARKET_CLOSED',
      reasonMessage: '정산이 완료된 시장입니다.',
      minBetAmount: 100,
      maxBetAmount: 5000,
      maxBetPerUser: 7000,
      perBetLimit: 2000,
    },
    myPosition: {
      side: 'NO',
      amount: 900,
      estimatedPayout: 0,
      status: 'SETTLED',
      result: 'WIN',
      finalPayout: 1302,
    },
  },
  'sol-upgrade-cancelled': {
    wallet: { ...baseWallet },
    eligibility: {
      canParticipate: false,
      reasonCode: 'MARKET_CANCELLED',
      reasonMessage: '취소된 시장으로 환불 처리만 확인할 수 있습니다.',
      minBetAmount: 100,
      maxBetAmount: 4000,
      maxBetPerUser: 6000,
      perBetLimit: 1500,
    },
    myPosition: {
      side: 'YES',
      amount: 500,
      estimatedPayout: 0,
      status: 'REFUNDED',
      result: 'VOID',
      finalPayout: null,
    },
  },
};

export const polymarketMockMarkets: PolymarketMarketSummary[] = [
  {
    id: 'btc-110k-mar',
    question: '2026년 3월 31일 23:59 UTC까지 BTC가 110,000달러 이상으로 마감할까?',
    category: 'PRICE_PREDICTION',
    status: 'OPEN',
    resolutionOutcome: 'PENDING',
    isOperatorApproved: true,
    closeAt: '2026-03-31T23:59:00Z',
    yesPool: 18400,
    noPool: 13200,
    totalPool: 31600,
    participantCount: 184,
    settlementSummary: '3월 31일 종가 기준으로 110,000달러 이상이면 YES로 정산합니다.',
    sourceLabel: 'CoinMarketCap BTC/USD',
    sourceUrl: 'https://coinmarketcap.com/currencies/bitcoin/',
  },
  {
    id: 'fed-cut-june',
    question: '2026년 6월 FOMC에서 기준금리 인하가 발표될까?',
    category: 'EVENT_OUTCOME',
    status: 'CLOSED',
    resolutionOutcome: 'PENDING',
    isOperatorApproved: true,
    closeAt: '2026-06-17T17:30:00Z',
    yesPool: 9400,
    noPool: 15100,
    totalPool: 24500,
    participantCount: 96,
    settlementSummary: '공식 FOMC 성명서 발표 시점을 기준으로 판정합니다.',
    sourceLabel: 'Federal Reserve Statement',
    sourceUrl: 'https://www.federalreserve.gov/',
  },
  {
    id: 'eth-etf-q2',
    question: '2026년 2분기 내 미국 현물 ETH ETF 추가 승인 발표가 있을까?',
    category: 'EVENT_OUTCOME',
    status: 'RESOLVED',
    resolutionOutcome: 'NO',
    isOperatorApproved: true,
    closeAt: '2026-06-30T23:00:00Z',
    yesPool: 8800,
    noPool: 19400,
    totalPool: 28200,
    participantCount: 121,
    settlementSummary: 'SEC 공식 승인 공지 부재로 NO로 정산되었습니다.',
    sourceLabel: 'SEC Release',
    sourceUrl: 'https://www.sec.gov/',
  },
  {
    id: 'sol-upgrade-cancelled',
    question: '2026년 4월 말 전 Solana 대형 네트워크 업그레이드가 예정대로 실행될까?',
    category: 'EVENT_OUTCOME',
    status: 'CANCELLED',
    resolutionOutcome: 'CANCELLED',
    isOperatorApproved: true,
    closeAt: '2026-04-29T12:00:00Z',
    yesPool: 6200,
    noPool: 7100,
    totalPool: 13300,
    participantCount: 54,
    settlementSummary: '기준 이벤트 정의가 변경되어 운영 취소되었습니다.',
    sourceLabel: 'Solana Foundation Updates',
    sourceUrl: 'https://solana.org/news',
  },
];

export const polymarketMockDetails: Record<string, PolymarketMarketDetail> = {
  'btc-110k-mar': {
    ...polymarketMockMarkets[0],
    description: '단기 가격 방향보다, 커뮤니티가 마감 시점까지 어떤 확신을 형성하는지 읽기 위한 질문입니다.',
    approvalBadgeType: 'operator-approved',
    resolvedAt: null,
    cancelledAt: null,
    resolutionBasisText: '2026년 3월 31일 23:59 UTC 이후 CoinMarketCap BTC/USD 종가가 110,000달러 이상이면 YES, 미만이면 NO입니다.',
    refundPolicyText: '시장 취소 시 참여 금액 전액이 환불됩니다.',
  },
  'fed-cut-june': {
    ...polymarketMockMarkets[1],
    description: '공식 발표를 근거로 판단하는 이벤트 결과형 시장입니다.',
    approvalBadgeType: 'operator-approved',
    resolvedAt: null,
    cancelledAt: null,
    resolutionBasisText: '2026년 6월 FOMC 성명서 또는 기자회견에서 기준금리 인하가 확정 발표되면 YES입니다.',
    refundPolicyText: '판정 불가 상황에서는 운영 취소 후 환불 처리됩니다.',
  },
  'eth-etf-q2': {
    ...polymarketMockMarkets[2],
    description: '운영 승인 후 공개된 시장이며 결과 확정까지의 근거를 그대로 보존합니다.',
    approvalBadgeType: 'operator-approved',
    resolvedAt: '2026-07-01T02:10:00Z',
    cancelledAt: null,
    resolutionBasisText: '2026년 2분기 종료 시점까지 SEC 추가 승인 공지가 없었으므로 NO입니다.',
    refundPolicyText: '시장 취소 시 전액 환불, 정산 완료 후에는 환불되지 않습니다.',
  },
  'sol-upgrade-cancelled': {
    ...polymarketMockMarkets[3],
    description: '운영 중 판정 기준을 유지하기 어렵다고 판단되어 공개 취소된 시장입니다.',
    approvalBadgeType: 'operator-approved',
    resolvedAt: null,
    cancelledAt: '2026-04-25T10:30:00Z',
    resolutionBasisText: '기준 이벤트의 일정과 판정 문구가 운영 승인 이후 변경되어 결과 판정 대신 취소 처리되었습니다.',
    refundPolicyText: '참여 금액 전액이 동일 토큰으로 환불되며, 추가 정산은 발생하지 않습니다.',
  },
};

const activePositions: PolymarketPositionListItem[] = [
  {
    participationId: 'part-active-001',
    marketId: 'btc-110k-mar',
    question: polymarketMockMarkets[0].question,
    side: 'YES',
    amount: 1200,
    status: 'PLACED',
    estimatedPayout: 2145,
    finalPayout: 0,
    closeAt: polymarketMockMarkets[0].closeAt,
    resolvedAt: null,
    result: 'ACTIVE',
  },
];

const settledPositions: PolymarketPositionListItem[] = [
  {
    participationId: 'part-settled-001',
    marketId: 'eth-etf-q2',
    question: polymarketMockMarkets[2].question,
    side: 'NO',
    amount: 900,
    status: 'SETTLED',
    estimatedPayout: 0,
    finalPayout: 1302,
    closeAt: polymarketMockMarkets[2].closeAt,
    resolvedAt: '2026-07-01T02:10:00Z',
    result: 'WIN',
  },
];

const refundedPositions: PolymarketPositionListItem[] = [
  {
    participationId: 'part-refunded-001',
    marketId: 'sol-upgrade-cancelled',
    question: polymarketMockMarkets[3].question,
    side: 'YES',
    amount: 500,
    status: 'REFUNDED',
    estimatedPayout: 0,
    finalPayout: 500,
    closeAt: polymarketMockMarkets[3].closeAt,
    resolvedAt: '2026-04-25T10:30:00Z',
    result: 'VOID',
  },
];

export const polymarketMockApi = {
  getMarkets(params?: { status?: string; category?: string; q?: string }): PolymarketMarketListResponse {
    // TRAD-1456 — backend는 question+description 둘 다 검색하지만, 목록 응답 타입
    // (PolymarketMarketSummary)엔 description이 없다(PolymarketMarketDetail 전용
    // 필드) — 이 mock은 네트워크 장애 시 폴백일 뿐이라 question만으로 최선 근사한다.
    const q = params?.q?.trim().toLowerCase();
    const items = polymarketMockMarkets.filter((market) => {
      const statusMatched = !params?.status || params.status === 'all' || market.status === params.status;
      const categoryMatched = !params?.category || params.category === 'all' || market.category === params.category;
      const qMatched = !q || market.question.toLowerCase().includes(q);
      return statusMatched && categoryMatched && qMatched;
    });

    const sortedItems = [...items].sort((left, right) => {
      const priorityByStatus = {
        OPEN: 0,
        CLOSED: 1,
        RESOLVED: 2,
        CANCELLED: 3,
        DRAFT: 4,
      } as const;

      if (priorityByStatus[left.status] !== priorityByStatus[right.status]) {
        return priorityByStatus[left.status] - priorityByStatus[right.status];
      }

      return new Date(left.closeAt).getTime() - new Date(right.closeAt).getTime();
    });

    return {
      items: sortedItems,
      nextCursor: null,
    };
  },

  getMarketDetail(marketId: string): PolymarketMarketDetailResponse {
    const market = polymarketMockDetails[marketId] ?? polymarketMockDetails['btc-110k-mar'];
    return {
      market,
      personalization: personalizationByMarketId[market.id] ?? null,
    };
  },

  getMeSummary(): PolymarketMeSummaryResponse {
    return {
      wallet: { ...baseWallet },
      activeCount: activePositions.length,
      settledCount: settledPositions.length,
      refundedCount: refundedPositions.length,
    };
  },

  getMyPositions(status: 'active' | 'settled' | 'refunded'): PolymarketPositionListResponse {
    const items = status === 'active' ? activePositions : status === 'settled' ? settledPositions : refundedPositions;
    return { items };
  },

  getQuote(marketId: string, request: PolymarketQuoteRequest): PolymarketQuoteResponse {
    const market = polymarketMockDetails[marketId] ?? polymarketMockDetails['btc-110k-mar'];
    const selectedPool = request.side === 'YES' ? market.yesPool : market.noPool;
    const updatedSelectedPool = selectedPool + request.amount;
    const updatedTotalPool = market.totalPool + request.amount;
    const estimatedPayout = updatedSelectedPool === 0
      ? 0
      : Number(((request.amount * updatedTotalPool) / updatedSelectedPool).toFixed(2));

    return {
      estimatedPayout,
      isReferenceOnly: true,
      pricingSnapshotVersion: `mock-${marketId}-${request.side}-${request.amount}`,
    };
  },

  submitProposal(_request: PolymarketProposalRequest): PolymarketProposalResponse {
    return {
      proposalId: `mock-proposal-${Date.now()}`,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    };
  },

  getMyProposals(): PolymarketMyProposalsResponse {
    return {
      items: [
        {
          proposalId: 'mock-proposal-001',
          title: '2026년 7월 말 전까지 BTC가 150,000달러를 돌파할까?',
          judgmentCriteria: 'CoinMarketCap BTC/USD 종가 기준으로 판정합니다.',
          sourceUrl: 'https://coinmarketcap.com/currencies/bitcoin/',
          category: 'PRICE_PREDICTION',
          closeAtHint: '2026-07-31T23:59:00',
          status: 'PENDING',
          rejectReason: null,
          createdAt: '2026-05-01T10:00:00Z',
          exceptionOutcomePolicy: null,
        },
        {
          proposalId: 'mock-proposal-002',
          title: '2026년 9월 FOMC 전까지 연준이 추가 금리 인하를 발표할까?',
          judgmentCriteria: 'Federal Reserve 공식 성명 기준으로 판정합니다.',
          sourceUrl: 'https://www.federalreserve.gov/',
          category: 'EVENT_OUTCOME',
          closeAtHint: '2026-09-15T17:30:00',
          status: 'REJECTED',
          rejectReason: '판정 기준이 모호하여 승인이 어렵습니다. 구체적인 금리 변동폭을 명시해주세요.',
          createdAt: '2026-04-20T14:00:00Z',
          exceptionOutcomePolicy: 'VOID_REFUND',
        },
      ],
    };
  },

  createParticipation(marketId: string, request: PolymarketParticipationRequest): PolymarketParticipationResponse {
    const market = polymarketMockDetails[marketId] ?? polymarketMockDetails['btc-110k-mar'];
    const quote = this.getQuote(marketId, request);
    const nextYesPool = request.side === 'YES' ? market.yesPool + request.amount : market.yesPool;
    const nextNoPool = request.side === 'NO' ? market.noPool + request.amount : market.noPool;
    const nextTotalPool = nextYesPool + nextNoPool;

    return {
      market: {
        id: market.id,
        yesPool: nextYesPool,
        noPool: nextNoPool,
        totalPool: nextTotalPool,
        participantCount: market.participantCount + 1,
      },
      myPosition: {
        side: request.side,
        amount: request.amount,
        status: 'PLACED',
        estimatedPayout: quote.estimatedPayout,
      },
      wallet: {
        availableBalance: Math.max(baseWallet.availableBalance - request.amount, 0),
        lockedBalance: baseWallet.lockedBalance + request.amount,
        pendingSettlementBalance: baseWallet.pendingSettlementBalance,
      },
    };
  },
};
